import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { startupDataService, type StartupDataSettings } from '@ideoz/core';
import { Button, Checkbox, toastError, toastSuccess } from '@ideoz/ui';
import { RolePicker } from './RolePicker';

/**
 * How visitors of the startup's published prototypes sign in and change data (US 598). Each
 * setting lifts one of the Phase 1 defaults: anyone with a link could write, sign-up always gave
 * role user, and a visitor signed in once per prototype link.
 */
export function StartupDataSettingsPanel({
  projectId,
  settings,
  onSaved,
}: {
  projectId: string;
  settings: StartupDataSettings | null;
  onSaved: (settings: StartupDataSettings) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<StartupDataSettings | null>(settings);
  const [saving, setSaving] = useState(false);
  const [usedRoles, setUsedRoles] = useState<string[]>([]);
  useEffect(() => setDraft(settings), [settings]);
  useEffect(() => {
    startupDataService
      .users(projectId)
      .then((users) => setUsedRoles(users.map((u) => String(u.record.role ?? ''))))
      .catch(() => setUsedRoles([]));
  }, [projectId]);

  if (!draft)
    return (
      <p className="p-6 text-sm text-muted-foreground">{t('startupData.settingsPanel.loading')}</p>
    );
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await startupDataService.saveSettings(projectId, draft);
      onSaved(saved);
      toastSuccess(t('startupData.settingsPanel.saved'));
    } catch (error) {
      toastError(
        error instanceof Error ? error.message : t('startupData.settingsPanel.saveFailed')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[640px] space-y-5 px-5 py-4">
      <Setting
        id="signed-in-writes-only"
        checked={draft.signedInWritesOnly}
        onChange={(v) => setDraft({ ...draft, signedInWritesOnly: v })}
        title={t('startupData.settingsPanel.signedInTitle')}
        detail={t('startupData.settingsPanel.signedInDetail')}
      />
      <Setting
        id="one-sign-in"
        checked={draft.oneSignIn}
        onChange={(v) => setDraft({ ...draft, oneSignIn: v })}
        title={t('startupData.settingsPanel.oneSignInTitle')}
        detail={t('startupData.settingsPanel.oneSignInDetail')}
      />
      <div className="space-y-1">
        <label htmlFor="default-role" className="text-sm font-medium">
          {t('startupData.settingsPanel.roleTitle')}
        </label>
        <p className="text-xs text-muted-foreground">{t('startupData.settingsPanel.roleDetail')}</p>
        <RolePicker
          id="default-role"
          value={draft.defaultRole}
          usedRoles={usedRoles}
          onChange={(role) => setDraft({ ...draft, defaultRole: role })}
        />
        <p className="text-xs text-muted-foreground">{t('startupData.roles.hint')}</p>
      </div>
      <Button onClick={() => void save()} disabled={!dirty || saving}>
        {saving ? t('startupData.settingsPanel.saving') : t('startupData.settingsPanel.save')}
      </Button>
    </div>
  );
}

function Setting({
  id,
  checked,
  onChange,
  title,
  detail,
}: {
  id: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  title: string;
  detail: string;
}): React.ReactElement {
  return (
    <div className="flex items-start gap-3">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
        className="mt-0.5"
      />
      <div className="space-y-0.5">
        <label htmlFor={id} className="text-sm font-medium">
          {title}
        </label>
        <p className="text-xs text-muted-foreground">{detail}</p>
      </div>
    </div>
  );
}

export default StartupDataSettingsPanel;
